import type {
  PDFDocumentProxy,
  RenderParameters,
} from "pdfjs-dist/types/src/display/api";
import { useEffect, useState } from "react";
import { loadPdfDocument } from "../../../helpers/pdfjs";
import { useFlashMessage } from "../../../hooks/useFlashMessage";

// NOTE: scale: 1 だと A4 1ページが約 595x842px になり文字が潰れるため、
//       表示枠（高さ 40rem）を ImageMagnifier の拡大率（3倍）で見ても読める解像度で描画する
const PDF_RENDER_SCALE = 3;
// NOTE: 大判の PDF でキャンバスが巨大にならないよう、長辺の画素数に上限を設ける
const PDF_RENDER_MAX_LONG_SIDE_PX = 4096;

interface UseAttachmentViewerProps {
  files: File[] | null;
  maxPageNo: number;
}

export interface ParsedFilesProps {
  cachedImageUrl: string;
  filename: string;
  file: File;
  numOfPages: number;
  // 「別タブで開く」で開くURL。PDF の場合は画像化前の元ファイルを開く
  originalFileUrl?: string;
}

interface PdfProps extends ParsedFilesProps {
  pdfDoc: PDFDocumentProxy;
  pdfPage: number;
}

export const useAttachmentViewer = (props: UseAttachmentViewerProps) => {
  const [parsedFiles, setParsedFiles] = useState<
    ParsedFilesProps[] | PdfProps[]
  >([]);
  const [numOfImages, setNumOfImages] = useState(0);
  const [pageNo, setPageNo] = useState(0);
  const [isParsing, setIsParsing] = useState(false);
  const availablePrev = pageNo >= 1;
  const availableNext =
    pageNo + 1 < parsedFiles.length && pageNo + 1 < props.maxPageNo;

  // cachedImageUrlに画像URLをセットする
  // NOTE: 画像URLの生成が画面描画処理のボトルネックになるため、画像が必要になった時に初めて画像URLを生成します
  useEffect(() => {
    const parsedFile = parsedFiles[pageNo];
    if (parsedFile?.cachedImageUrl || !parsedFile?.file) {
      return;
    }

    if (parsedFile.file.type === "application/pdf") {
      const pdfDoc = (parsedFile as PdfProps).pdfDoc;
      const pdfPage = (parsedFile as PdfProps).pdfPage;
      getImageDataUrl(pdfDoc, pdfPage).then((url) => {
        setParsedFiles((prev) => {
          const newParsedFiles = [...prev];
          if (!newParsedFiles[pageNo]) {
            return prev;
          }
          newParsedFiles[pageNo].cachedImageUrl = url;
          return newParsedFiles;
        });
      });
    } else {
      setParsedFiles((prev) => {
        const newParsedFiles = [...prev];
        if (!newParsedFiles[pageNo]) {
          return prev;
        }
        newParsedFiles[pageNo].cachedImageUrl = window.URL.createObjectURL(
          parsedFile.file,
        );
        return newParsedFiles;
      });
    }
  }, [parsedFiles[pageNo]?.cachedImageUrl]);

  useEffect(() => {
    return () => {
      // NOTE: メモリリークを防ぐため、画像URLを解放します
      //       https://developer.mozilla.org/ja/docs/Web/API/URL/createObjectURL_static#%E3%83%A1%E3%83%A2%E3%83%AA%E3%83%BC%E7%AE%A1%E7%90%86
      parsedFiles.forEach((parsedFile) => {
        URL.revokeObjectURL(parsedFile.cachedImageUrl);
      });
    };
  }, []);
  const { showErrorMessage } = useFlashMessage();

  useEffect(() => {
    async function convertToImage() {
      setIsParsing(true);
      const newParsedFiles: ParsedFilesProps[] = [];
      let parsedNumOfImages = 0;
      if (props.files) {
        for (const file of props.files) {
          const cachedParsedFiles = parsedFiles.filter(
            (parsedFile) => parsedFile.file === file,
          );
          if (cachedParsedFiles.length === 0) {
            switch (file.type) {
              case "application/pdf": {
                const files = await getAllParsedFiles(
                  file,
                  props.maxPageNo,
                ).catch(() => {
                  showErrorMessage("ファイルの読み込みに失敗しました。");
                  return [] as ParsedFilesProps[];
                });
                newParsedFiles.push(...files);
                if (files.length > 0) {
                  parsedNumOfImages += files[0].numOfPages;
                }
                break;
              }
              case "image/jpg":
              case "image/jpeg":
              case "image/png": {
                newParsedFiles.push({
                  cachedImageUrl: "",
                  filename: file.name,
                  file: file,
                  numOfPages: 1,
                });
                parsedNumOfImages++;
              }
            }
          } else {
            newParsedFiles.push(...cachedParsedFiles);
            parsedNumOfImages += cachedParsedFiles[0].numOfPages;
          }
        }
      }
      setParsedFiles(newParsedFiles);
      setNumOfImages(parsedNumOfImages);
      setIsParsing(false);
    }
    convertToImage();
  }, [props.files?.map((file) => file.name + file.lastModified).join(",")]);

  useEffect(() => {
    setPageNo(0);
  }, [parsedFiles.length]);

  const isCurrentFile = (file: File) => {
    if (parsedFiles.length === 0 || !parsedFiles[pageNo]) {
      return false;
    }
    return parsedFiles[pageNo].file === file;
  };

  return {
    parsedFiles,
    numOfImages,
    pageNo,
    setPageNo,
    availablePrev,
    availableNext,
    isCurrentFile,
    isParsing,
  };
};

const getAllParsedFiles = async (pdfFile: File, maxPageNo: number) => {
  const pdfFileUrl = window.URL.createObjectURL(pdfFile);
  const pdfDoc = await loadPdfDocument(pdfFileUrl).promise.catch(() => {
    throw new Error("pdfjsLib.getDocument failed");
  });
  const parsedFiles: ParsedFilesProps[] = [];
  for (let i = 1; i <= Math.min(pdfDoc._pdfInfo.numPages, maxPageNo); i++) {
    parsedFiles.push({
      cachedImageUrl: "",
      filename: pdfFile.name,
      file: pdfFile,
      numOfPages: pdfDoc._pdfInfo.numPages,
      originalFileUrl: pdfFileUrl,
      pdfDoc,
      pdfPage: i,
    } as PdfProps);
  }
  return parsedFiles;
};

const getImageDataUrl = async (pdf: PDFDocumentProxy, pageNo: number) => {
  const page = await pdf.getPage(pageNo);
  const baseViewport = page.getViewport({ scale: 1 });
  const scale = Math.min(
    PDF_RENDER_SCALE,
    PDF_RENDER_MAX_LONG_SIDE_PX /
      Math.max(baseViewport.width, baseViewport.height),
  );
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  canvas.width = viewport.width;
  canvas.height = viewport.height;

  if (context) {
    const renderContext: RenderParameters = {
      canvasContext: context,
      viewport: viewport,
      canvas: canvas,
    };
    await page.render(renderContext).promise;
  }
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/png"),
  );
  if (!blob) {
    return "";
  }
  return URL.createObjectURL(blob);
};
